import { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2, UploadCloud, CheckCircle2, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  useCreatePagamento,
  uploadFile,
  type FormaPagamento,
  type CreatePagamentoInput,
} from '@/lib/hooks/pagamentos';
import { cn, fmtCpfCnpj } from '@/lib/utils';

interface FormValues {
  competencia: string;
  credorCpfCnpj: string;
  credorNome: string;
  valor: string;
  dataPagamento: string;
  formaPagamento: FormaPagamento | '';
  categoriaDespesa: string;
  folhaOrdinaria: boolean;
  comprovanteR2Key: string;
}

interface Props {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultCompetencia?: string;
}

const FORMAS: { value: FormaPagamento; label: string }[] = [
  { value: 'PIX', label: 'PIX' },
  { value: 'TED', label: 'TED' },
  { value: 'DOC', label: 'DOC' },
  { value: 'BOLETO', label: 'Boleto' },
  { value: 'DINHEIRO', label: 'Dinheiro' },
  { value: 'CHEQUE', label: 'Cheque' },
];

const ACCEPT = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

// ─── Dropzone ─────────────────────────────────────────────────────────────────

type DropzoneState = 'idle' | 'uploading' | 'success' | 'dragOver';

interface DropzoneProps {
  state: DropzoneState;
  uploadedName: string | null;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onClick: () => void;
  onRemove: () => void;
}

function Dropzone({
  state,
  uploadedName,
  onDrop,
  onDragOver,
  onDragLeave,
  onClick,
  onRemove,
}: DropzoneProps) {
  if (state === 'success') {
    return (
      <div
        className="flex items-center gap-3 rounded-2 border-2 border-pass bg-pass-soft/40 px-4 py-4"
        role="status"
        aria-live="polite"
      >
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-pass/10 text-pass">
          <CheckCircle2 className="h-5 w-5" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-fg truncate">
            {uploadedName ?? 'arquivo enviado'}
          </p>
          <p className="text-[11px] text-fg-muted mt-0.5">enviado com sucesso</p>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 text-xs text-fg-muted hover:text-fg underline underline-offset-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint-deep rounded-1 px-1"
          aria-label="Trocar comprovante"
        >
          Trocar
        </button>
      </div>
    );
  }

  const isDragOver = state === 'dragOver';
  const isUploading = state === 'uploading';

  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={isUploading ? undefined : onClick}
      role={isUploading ? 'status' : 'button'}
      aria-live={isUploading ? 'polite' : undefined}
      aria-label={isUploading ? 'Enviando arquivo' : 'Clique ou arraste para enviar comprovante'}
      tabIndex={isUploading ? -1 : 0}
      onKeyDown={(e) => {
        if (!isUploading && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-2.5 rounded-2 border-2 border-dashed px-4 py-8 transition-colors duration-fast',
        isUploading
          ? 'border-mint-deep bg-mint-soft cursor-default'
          : isDragOver
            ? 'border-mint-deep bg-mint-soft/60 cursor-copy'
            : 'border-card-stroke bg-card-soft cursor-pointer hover:border-mint-deep/60 hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint-deep focus-visible:ring-offset-2',
      )}
    >
      <div
        className={cn(
          'grid h-12 w-12 place-items-center rounded-full transition-colors',
          isUploading || isDragOver
            ? 'bg-mint-deep/10 text-mint-deep'
            : 'bg-card text-fg-muted',
        )}
      >
        {isUploading ? (
          <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.8} />
        ) : (
          <UploadCloud className="h-5 w-5" strokeWidth={1.6} />
        )}
      </div>

      <div className="text-center">
        <p className={cn('text-sm font-semibold', isUploading ? 'text-mint-deep' : 'text-fg')}>
          {isUploading
            ? `Enviando…`
            : isDragOver
              ? 'Solte para enviar'
              : 'Arraste o comprovante aqui'}
        </p>
        {!isUploading && (
          <p className="text-[11px] text-fg-muted mt-0.5">
            ou clique para escolher · PDF/JPG/PNG · até 10 MB
          </p>
        )}
      </div>
    </div>
  );
}

// ─── PagamentoDrawer ──────────────────────────────────────────────────────────

export function PagamentoDrawer({ workspaceId, open, onOpenChange, defaultCompetencia }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedName, setUploadedName] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const createMut = useCreatePagamento(workspaceId);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      competencia: defaultCompetencia ?? '',
      credorCpfCnpj: '',
      credorNome: '',
      valor: '',
      dataPagamento: '',
      formaPagamento: '',
      categoriaDespesa: '',
      folhaOrdinaria: false,
      comprovanteR2Key: '',
    },
  });

  useEffect(() => {
    if (!open) {
      reset({
        competencia: defaultCompetencia ?? '',
        credorCpfCnpj: '',
        credorNome: '',
        valor: '',
        dataPagamento: '',
        formaPagamento: '',
        categoriaDespesa: '',
        folhaOrdinaria: false,
        comprovanteR2Key: '',
      });
      setUploadedName(null);
      setUploading(false);
      setDragOver(false);
    }
  }, [open, defaultCompetencia, reset]);

  const r2Key = watch('comprovanteR2Key');

  // Resolve estado da dropzone
  const dropzoneState: DropzoneState = r2Key
    ? 'success'
    : uploading
      ? 'uploading'
      : dragOver
        ? 'dragOver'
        : 'idle';

  async function handleFile(file: File) {
    if (!ACCEPT.includes(file.type)) {
      toast.error('Formato não suportado. Use PDF, JPG, PNG ou WebP.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo excede 10 MB.');
      return;
    }
    setUploading(true);
    try {
      const key = await uploadFile(file, 'comprovantes');
      setValue('comprovanteR2Key', key, { shouldDirty: true });
      setUploadedName(file.name);
      toast.success('Comprovante enviado.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha no upload.');
    } finally {
      setUploading(false);
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(true);
  }

  function removeFile() {
    setValue('comprovanteR2Key', '', { shouldDirty: true });
    setUploadedName(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function onSubmit(values: FormValues) {
    const payload: CreatePagamentoInput = {
      competencia: values.competencia,
      credorCpfCnpj: values.credorCpfCnpj,
      credorNome: values.credorNome,
      valor: values.valor.replace(',', '.'),
      dataPagamento: values.dataPagamento,
      folhaOrdinaria: values.folhaOrdinaria,
    };
    if (values.formaPagamento) payload.formaPagamento = values.formaPagamento;
    if (values.categoriaDespesa) payload.categoriaDespesa = values.categoriaDespesa;
    if (values.comprovanteR2Key) payload.comprovanteR2Key = values.comprovanteR2Key;

    try {
      await createMut.mutateAsync(payload);
      toast.success('Pagamento cadastrado.');
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Falha ao cadastrar pagamento.';
      toast.error(msg);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <SheetHeader
            eyebrow="Novo lançamento"
            title="Cadastrar pagamento"
            description="Suba o comprovante ou preencha manualmente. Você pode editar depois."
          />

          <SheetBody className="space-y-5">
            {/* Dropzone — primeira coisa do form */}
            <div>
              <Label className="mb-2 block font-semibold">
                Comprovante{' '}
                <span className="text-fg-dim font-normal">(opcional)</span>
              </Label>
              <Dropzone
                state={dropzoneState}
                uploadedName={uploadedName}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={() => setDragOver(false)}
                onClick={() => fileInputRef.current?.click()}
                onRemove={removeFile}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT.join(',')}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
              />
            </div>

            {/* Divider */}
            <div className="border-t border-card-stroke" />

            {/* Campos */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="competencia" className="mb-1.5 block font-semibold">
                  Competência
                </Label>
                <Input
                  id="competencia"
                  placeholder="2026-05"
                  {...register('competencia', {
                    required: 'Obrigatório',
                    pattern: { value: /^\d{4}-\d{2}$/, message: 'Formato YYYY-MM' },
                  })}
                />
                {errors.competencia && (
                  <p className="text-[11px] text-warn mt-1">{errors.competencia.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="dataPagamento" className="mb-1.5 block font-semibold">
                  Data de pagamento
                </Label>
                <Input
                  id="dataPagamento"
                  type="date"
                  {...register('dataPagamento', { required: 'Obrigatório' })}
                />
                {errors.dataPagamento && (
                  <p className="text-[11px] text-warn mt-1">{errors.dataPagamento.message}</p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="credorNome" className="mb-1.5 block font-semibold">
                Credor (nome)
              </Label>
              <Input
                id="credorNome"
                placeholder="Ex.: Padaria Estrela"
                {...register('credorNome', {
                  required: 'Obrigatório',
                  minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                })}
              />
              {errors.credorNome && (
                <p className="text-[11px] text-warn mt-1">{errors.credorNome.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="credorCpfCnpj" className="mb-1.5 block font-semibold">
                  CPF / CNPJ
                </Label>
                <Input
                  id="credorCpfCnpj"
                  placeholder="000.000.000-00"
                  {...register('credorCpfCnpj', {
                    required: 'Obrigatório',
                    setValueAs: (v: string) => fmtCpfCnpj(v),
                  })}
                />
                {errors.credorCpfCnpj && (
                  <p className="text-[11px] text-warn mt-1">{errors.credorCpfCnpj.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="valor" className="mb-1.5 block font-semibold">
                  Valor (R$)
                </Label>
                <Input
                  id="valor"
                  inputMode="decimal"
                  placeholder="1234.56"
                  {...register('valor', {
                    required: 'Obrigatório',
                    pattern: { value: /^\d+([.,]\d{1,2})?$/, message: 'Valor inválido' },
                  })}
                />
                <p className="text-[11px] text-fg-muted mt-0.5">Sem R$, ex: 1234.56</p>
                {errors.valor && (
                  <p className="text-[11px] text-warn mt-1">{errors.valor.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block font-semibold">Forma de pagamento</Label>
                <Controller
                  control={control}
                  name="formaPagamento"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione…" />
                      </SelectTrigger>
                      <SelectContent>
                        {FORMAS.map((f) => (
                          <SelectItem key={f.value} value={f.value}>
                            {f.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div>
                <Label htmlFor="categoria" className="mb-1.5 block font-semibold">
                  Categoria
                </Label>
                <Input
                  id="categoria"
                  placeholder="Ex.: Material de consumo"
                  {...register('categoriaDespesa')}
                />
                <p className="text-[11px] text-fg-muted mt-0.5">Tipo de despesa</p>
              </div>
            </div>

            <label className="flex items-center gap-2.5 text-sm text-fg cursor-pointer select-none">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-card-stroke accent-mint-deep"
                {...register('folhaOrdinaria')}
              />
              <span>
                Faz parte da{' '}
                <span className="font-semibold">folha ordinária</span>
              </span>
            </label>
          </SheetBody>

          <SheetFooter>
            <SheetClose asChild>
              <Button variant="ghost" type="button">
                Cancelar
              </Button>
            </SheetClose>
            <Button variant="primary" type="submit" disabled={isSubmitting || uploading}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                  Salvando…
                </>
              ) : (
                'Cadastrar pagamento'
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
